/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable, InjectionToken, Injector, signal} from '@angular/core';
import * as _ from 'lodash';
import {firstValueFrom, Observable} from 'rxjs';

import {LocalUserProfile} from './session/local-user-profile';

export interface WorkspaceRegistryApi {
  list(params: any): Observable<{results: any[]}>;
}

export const WORKSPACE_REGISTRY_API = new InjectionToken<WorkspaceRegistryApi>('WorkspaceRegistryApi');

@Injectable({
  providedIn: 'root'
})
export class WorkspaceRegistryService {
  private readonly workspacesState = signal<any[]>([]);

  get workspaces(): any[] {
    return this.workspacesState();
  }

  $promise: any;

  constructor(
    private readonly injector: Injector,
    private readonly localUserProfile: LocalUserProfile
  ) {
  }

  sync(): any {
    if (this.localUserProfile.getCurrentApp() !== 'ADVERTISING') {
      return Promise.resolve();
    }
    const api = this.injector.get(WORKSPACE_REGISTRY_API);
    this.$promise = firstValueFrom(api.list({
      includeHidden: false,
      includeActive: true,
      includeInactive: false,
      orderBy: 'name',
      orderByDirection: 'ASCENDING',
      page: 1,
      pageSize: 1001,
      concise: true
    })).then((page) => {
      this.workspacesState.set(_.sortBy(page.results, sortByName));
      return this.workspaces;
    });
    return this.$promise;
  }

  getPromise(): any {
    return this.$promise || this.sync();
  }

  remove(workspace: any): any {
    const removed = _.filter(this.workspaces, ['id', workspace.id]);
    if (removed.length) {
      this.workspacesState.set(_.reject(this.workspaces, ['id', workspace.id]));
    }
    return removed;
  }

  update(workspace: any): void {
    const workspaces = _.reject(this.workspaces, ['id', workspace.id]);
    this.workspacesState.set(_.sortBy([workspace, ...workspaces], sortByName));
  }
}

function sortByName(ws: any): string {
  return (ws.name || '').toLowerCase();
}
